import { createSlice } from '@reduxjs/toolkit'
import { Collection } from 'constants/index'
import { caseInsensitiveSort, getBuildingTitle } from 'utils'
import { FirestoreDatabase, storageRef } from '../index'
import { signOut } from 'store/auth'

export const initialState = {
  error: null,
  inProgress: false,
  buildings: [],
  building: null,
  currentPage: 0,
  pageSize: 10
}

const buildingSlice = createSlice({
  name: 'building',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setBuildings: (state, action) => {
      state.buildings = action.payload
    },
    setBuilding: (state, action) => {
      state.building = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.buildings = []
      state.building = null
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const addBuilding = (payload) => async (dispatch, getState) => {
  try {
    const user = getState().auth.user
    dispatch(buildingSlice.actions.setProgress(true))
    const doc = await FirestoreDatabase.collection(Collection.BUILDINGS).add({
      ...payload,
      conferenceId: user?.conferenceId
    })
    return doc.id
  } catch (err) {
    dispatch(buildingSlice.actions.setError(err))
  } finally {
    dispatch(buildingSlice.actions.setProgress(false))
  }
}

export const fetchBuildings = (conferenceId, callback) => {
  try {
    return FirestoreDatabase.collection(Collection.BUILDINGS)
      .where('conferenceId', '==', conferenceId)
      .onSnapshot((documents) => {
        const buildings = []
        documents.forEach((doc) => {
          buildings.push({
            id: doc.id,
            ...doc.data(),
            buildingText: getBuildingTitle(
              doc.data()?.title,
              doc.data()?.floorNum,
              doc.data()?.roomNum
            )
          })
        })
        callback(caseInsensitiveSort(buildings, 'title'))
      })
  } catch (err) {
    console.error(err)
  }
}

export const getConfBuildings = async (conferenceId) => {
  try {
    const snapshot = await FirestoreDatabase.collection(Collection.BUILDINGS)
      .where('conferenceId', '==', conferenceId)
      .get()

    const buildings = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      buildingText: getBuildingTitle(doc.data()?.title, doc.data()?.floorNum, doc.data()?.roomNum)
    }))

    return caseInsensitiveSort(buildings, 'title')
  } catch (err) {
    console.error(err)
  }
}

export const fetchBuildingById = (buildingId) => async (dispatch) => {
  try {
    dispatch(buildingSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.BUILDINGS).doc(buildingId).get()
    const e = snapshot.data()
    dispatch(
      buildingSlice.actions.setBuilding({
        id: snapshot.id,
        ...e
      })
    )
  } catch (err) {
    dispatch(buildingSlice.actions.setError(err))
  } finally {
    dispatch(buildingSlice.actions.setProgress(false))
  }
}

export const checkBuildingExistsInEvents = (buildingId) => async (dispatch, getState) => {
  try {
    dispatch(buildingSlice.actions.setProgress(true))
    const user = getState().auth.user
    const snapshot = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('conferenceId', '==', user?.conferenceId)
      .where('venueId', '==', buildingId)
      .get()

    return !snapshot.empty
  } catch (err) {
    dispatch(buildingSlice.actions.setError(err))
  } finally {
    dispatch(buildingSlice.actions.setProgress(false))
  }
}

export const checkBuildingExistsInTalks = (buildingId) => async (dispatch, getState) => {
  try {
    dispatch(buildingSlice.actions.setProgress(true))
    const user = getState().auth.user
    const snapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('conferenceId', '==', user?.conferenceId)
      .where('venueId', '==', buildingId)
      .get()

    return !snapshot.empty
  } catch (err) {
    dispatch(buildingSlice.actions.setError(err))
  } finally {
    dispatch(buildingSlice.actions.setProgress(false))
  }
}

export const updateBuildingById = (buildingId, payload) => async (dispatch) => {
  try {
    dispatch(buildingSlice.actions.setProgress(true))
    await FirestoreDatabase.collection(Collection.BUILDINGS).doc(buildingId).update(payload)
    return true
  } catch (err) {
    dispatch(buildingSlice.actions.setError(err))
  } finally {
    dispatch(buildingSlice.actions.setProgress(false))
  }
}

export const deleteBuildingById = (conferenceId, buildingId) => async (dispatch) => {
  try {
    const snapshot = await FirestoreDatabase.collection(Collection.BUILDINGS).doc(buildingId).get()
    const e = snapshot.data()
    if (e.imageName) {
      try {
        await storageRef.child(`${conferenceId}/${Collection.BUILDINGS}/${e.imageName}`).delete()
      } catch (e) {
        // do nothing
      }
    }
    await FirestoreDatabase.collection(Collection.BUILDINGS).doc(buildingId).delete()
  } catch (err) {
    console.error(err)
    dispatch(buildingSlice.actions.setError(err))
  }
}

export const bulkInsertBuildings = (buildings) => async (dispatch) => {
  try {
    const collectionRef = FirestoreDatabase.collection(Collection.BUILDINGS)
    const batch = FirestoreDatabase.batch()
    buildings.forEach((docData) => {
      const docRef = collectionRef.doc()
      batch.set(docRef, docData)
    })
    await batch.commit()
  } catch (err) {
    console.log('err', err)
    dispatch(buildingSlice.actions.setError(err))
  }
}

export const resetBuilding = () => (dispatch) => dispatch(buildingSlice.actions.setBuilding(null))

export default buildingSlice
